<template>
  <div class="patchk-wrapper">
    <!-- 新增导航栏 -->
    <nav class="navbar">
      <div class="navbar-left">
        <button class="back-btn">
          <i class="fas fa-arrow-left"></i>
        </button>
      </div>
      <!-- 在navbar-center部分找到logo-icon并修改 -->
      <div class="navbar-center">
        <div class="logo-container">
          <div class="logo-icon">
            <img src="@/assets/PR_logo_small.png" alt="DiOrion Logo" />
          </div>
          <div class="logo-text">
            <h1>DiOrion-PatChk</h1>
            <p>Patent Coverage Analysis</p>
          </div>
        </div>
      </div>
      <div class="navbar-right">
        <button class="nav-btn">
          <i class="fas fa-question-circle"></i> Help
        </button>
        <button class="nav-btn">
          <i class="fas fa-cog"></i> Settings
        </button>
      </div>
    </nav>
    
    <div class="patchk-container">
      <!-- 左侧栏：占比 2/5 -->
      <div class="left-column">
        <!-- 新增水平容器，用于放置专利文档上传和分子结构上传区域 -->
        <div class="upload-section-container">
          <!-- 专利文档上传区域 -->
          <div class="section patent-document">
            <h3>Patent Document</h3>
            <div class="upload-area">
              <div class="upload-icon blue-icon">
                <i class="fas fa-cloud-upload-alt"></i>
              </div>
              <p>Upload Patent PDF</p>
              <p class="subtext">Drag and drop your PDF file here, or click to browse</p>
              <button class="choose-file-btn blue-btn" @click="selectPatentFile">+ Choose File</button>
              <!-- 添加文件选择的隐藏input -->
              <input type="file" ref="patentFileInput" style="display: none;" accept=".pdf" @change="handlePatentFileSelect">
              <!-- 显示已选文件信息 -->
              <div v-if="selectedPatentFile" class="selected-file-info">
                <p>{{ selectedPatentFile.name }}</p>
                <button class="remove-file-btn" @click="removePatentFile">Remove</button>
              </div>
              <p class="file-size">Maximum file size: 10MB</p>
            </div>
          </div>

          <!-- 分子结构上传/绘制区域 -->
          <div class="section molecular-structure">
            <h3>Molecular Structure</h3>
            <div class="upload-area">
              <div class="upload-icon green-icon">
                <i class="fas fa-cloud-upload-alt"></i>
              </div>
              <p>Upload or Draw Molecule</p>
              <p class="subtext">Drag, drop and draw your molecular here</p>
              <div class="btn-group">
                <button class="choose-file-btn green-btn" @click="selectMoleculeFile">+ Choose File</button>
                <button class="draw-btn green-btn" @click="drawMolecule">
                  <i class="fas fa-pencil-alt"></i> Draw
                </button>
              </div>
              <!-- 添加文件选择的隐藏input -->
              <input type="file" ref="moleculeFileInput" style="display: none;" accept=".sdf,.mol,.mol2,.png,.jpg,.jpeg" @change="handleMoleculeFileSelect">
              <!-- 显示已选文件信息 -->
              <div v-if="selectedMoleculeFile" class="selected-file-info">
                <p>{{ selectedMoleculeFile.name }}</p>
                <button class="remove-file-btn" @click="removeMoleculeFile">Remove</button>
              </div>
              <p class="file-formats">Supports SDF, MOL, MOL2, PNG, JPG, JPEG formats</p>
            </div>
          </div>
        </div>

        <!-- 结构预览区域 -->
        <div class="section structure-preview">
          <h3>Structure Preview</h3>
          <div class="preview-area">
            <div v-if="previewImage" class="preview-image-container">
              <img :src="previewImage" alt="Molecular Structure Preview" class="preview-image">
            </div>
            <div v-else class="beaker-icon">
              <i class="fas fa-flask"></i>
            </div>
            <p v-if="!previewImage">Molecular structure will appear here</p>
            <p class="subtext" v-if="!previewImage">Upload an image to see the preview</p>
          </div>
        </div>

        <!-- 开始分析按钮 -->
        <button class="start-analysis-btn" @click="startAnalysis" :disabled="!canStartAnalysis">
          <i class="fas fa-search"></i> Start Analysis
        </button>
      </div>

      <!-- 右侧栏：占比 3/5 -->
      <div class="right-column">
        <div class="analysis-header">
          <h2>
            <i class="fas fa-chart-line"></i> Analysis Results
          </h2>
          <p>Patent coverage analysis and molecular structure comparison</p>
          <button class="clear-results-btn" @click="clearResults">
            <i class="fas fa-sync-alt"></i> Clear Results
          </button>
        </div>
        <div class="analysis-ready" v-if="!analysisResults">
          <i class="fas fa-list"></i>
          <h3>Ready for Analysis</h3>
          <p>Upload your documents and click "Start Analysis" to begin</p>
        </div>
        <div class="analysis-results" v-else>
          <!-- 分析结果的显示区域 -->
          <div id="conversation" ref="conversation"></div>
          <div id="loadingIndicator" ref="loadingIndicator" class="loading-indicator hidden">
            <div class="loading-spinner"></div>
          </div>
          <div id="statusBar" ref="statusBar" class="status-bar hidden">
            <span id="statusText" ref="statusText"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify';
import { marked } from 'marked';

export default {
  name: 'PatChk',
  data() {
    return {
      selectedPatentFile: null,
      selectedMoleculeFile: null,
      previewImage: null,
      analysisResults: null,
      // 模拟上传进度
      uploadProgress: 0,
      // 分析状态变量
      thinkingContent: '',
      fullAnswerContent: '', // 存储原始的Markdown文本
      assistantMessageElement: null,
      thinkingElement: null,
      // 用于节流渲染的状态变量
      isRenderScheduled: false
    }
  },
  computed: {
    // 计算是否可以开始分析
    canStartAnalysis() {
      return this.selectedPatentFile && this.selectedMoleculeFile
    }
  },
  methods: {
    // 选择专利文件
    selectPatentFile() {
      this.$refs.patentFileInput.click()
    },
    
    // 选择分子结构文件
    selectMoleculeFile() {
      this.$refs.moleculeFileInput.click()
    },
    
    // 处理专利文件选择
    handlePatentFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        // 文件大小验证（10MB）
        if (file.size > 10 * 1024 * 1024) {
          alert('File size exceeds 10MB limit')
          return
        }
        
        // 文件类型验证
        if (!file.type.includes('pdf')) {
          alert('Please select a PDF file')
          return
        }
        
        this.selectedPatentFile = file
        // 这里可以添加实际的文件上传逻辑
        this.uploadFile(file, 'patent')
      }
    },
    
    // 处理分子结构文件选择
    handleMoleculeFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.selectedMoleculeFile = file
        // 如果是图片文件，生成预览
        if (['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
          this.generatePreview(file)
        }
        // 这里可以添加实际的文件上传逻辑
        this.uploadFile(file, 'molecule')
      }
    },
    
    // 生成图片预览
    generatePreview(file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.previewImage = e.target.result
      }
      reader.readAsDataURL(file)
    },
    
    // 上传文件（模拟）
    uploadFile(file, type) {
      // 这里应该是实际的文件上传逻辑，这里用setTimeout模拟
      this.uploadProgress = 0
      
      const interval = setInterval(() => {
        this.uploadProgress += 10
        if (this.uploadProgress >= 100) {
          clearInterval(interval)
          // 上传完成后的处理
          console.log(`${type} file uploaded:`, file.name)
        }
      }, 200)
    },
    
    // 绘制分子结构
    drawMolecule() {
      // 这里可以添加分子结构绘制的逻辑
      // 由于没有具体的分子编辑器库，这里只是模拟一个简单的提示
      alert('This feature would open a molecule editor.\nIn a real application, you would integrate a specialized molecule drawing library.')
    },
    
    // 移除专利文件
    removePatentFile() {
      this.selectedPatentFile = null
      this.$refs.patentFileInput.value = ''
    },
    
    // 移除分子结构文件
    removeMoleculeFile() {
      this.selectedMoleculeFile = null
      this.previewImage = null
      this.$refs.moleculeFileInput.value = ''
    },
    
    // 开始分析
    async startAnalysis() {
      if (!this.canStartAnalysis) return
      
      // --- 初始化和状态变量 ---
      const conversation = this.$refs.conversation
      const loadingIndicator = this.$refs.loadingIndicator
      const statusBar = this.$refs.statusBar
      const statusText = this.$refs.statusText
      
      if (conversation) conversation.innerHTML = ''
      if (loadingIndicator) loadingIndicator.classList.remove('hidden')
      if (statusBar) statusBar.classList.remove('hidden')
      if (statusText) statusText.textContent = 'Uploading the file and initializing the analysis...'
      
      this.thinkingContent = ''
      this.fullAnswerContent = '' // 存储原始的Markdown文本
      this.assistantMessageElement = null
      this.thinkingElement = null
      this.isRenderScheduled = false
      
      this.analysisResults = {}
      
      try {
        // 模拟分析过程
        await this.simulateAnalysisProcess()
      } catch (error) {
        console.error('Analysis error:', error)
        if (statusText) statusText.textContent = 'Analysis failed'
      } finally {
        if (loadingIndicator) loadingIndicator.classList.add('hidden')
      }
    },
    
    // 模拟分析过程
    async simulateAnalysisProcess() {
      // 模拟文件上传和初始化分析
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // 更新状态
      if (this.$refs.statusText) {
        this.$refs.statusText.textContent = 'Analyzing patent content...'
      }
      
      // 模拟思考过程
      this.updateThinkingStatus('Extracting key information from patent document...\n')
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      this.updateThinkingStatus('Identifying molecular structures mentioned in the patent...\n')
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      this.updateThinkingStatus('Comparing uploaded molecular structure with patent claims...\n')
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      this.updateThinkingStatus('Evaluating coverage and potential infringements...\n')
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // 模拟生成答案
      if (this.$refs.statusText) {
        this.$refs.statusText.textContent = 'Generating analysis report...'
      }
      
      // 创建最终答案的消息元素
      this.assistantMessageElement = this.createAssistantMessage()
      
      // 模拟分块更新答案
      const answerChunks = [
        '# Patent Coverage Analysis\n\n',
        '## Summary\n\n',
        'The uploaded molecular structure **[Example Molecule]** has been analyzed against the patent document **[Example Patent.pdf]**.\n\n',
        '## Coverage Assessment\n\n',
        'The analysis indicates that the uploaded molecule falls within the scope of claims 1-3 of the patent. The key structural features match:\n\n',
        '- The core molecular backbone\n',
        '- Functional groups in positions 2 and 5\n',
        '- Stereochemical configuration at the chiral center\n\n',
        '## Chemical Formula Comparison\n\n',
        'The molecular formula of the uploaded structure is $C_{18}H_{21}NO_3$, which matches the formula specified in claim 2 of the patent.\n\n',
        '## Recommendations\n\n',
        'Based on this analysis, it is recommended to:\n\n',
        '1. Consult with a patent attorney to review the specific claims\n',
        '2. Consider potential design-around strategies\n',
        '3. Evaluate the validity of the patent\n\n',
        '## Confidence Level\n\n',
        'The confidence level of this analysis is **high** (92%).'
      ]
      
      for (const chunk of answerChunks) {
        this.updateAssistantMessage(chunk)
        await new Promise(resolve => setTimeout(resolve, 300))
      }
      
      // 分析完成
      if (this.$refs.statusText) {
        this.$refs.statusText.textContent = 'Analysis completed'
      }
      
      // 延迟隐藏状态栏
      await new Promise(resolve => setTimeout(resolve, 2000))
      if (this.$refs.statusBar) {
        this.$refs.statusBar.classList.add('hidden')
      }
    },
    
    // 创建思考过程元素
    createThinkingElement() {
      const conversation = this.$refs.conversation
      if (!conversation) return null
      
      const element = document.createElement('div')
      element.className = 'message assistant-message thinking-message p-4 md:p-6 bg-gray-50 border border-gray-200 rounded-xl max-w-7xl mx-auto mt-4 fade-in'
      
      element.innerHTML = `
        <div class="flex items-start space-x-4">
          <div class="text-xl pt-1"><i class="fas fa-brain fa-fade"></i></div>
          <div class="flex-1">
            <div class="thinking-header flex justify-between items-center cursor-pointer">
              <span class="thinking-label inline-block bg-yellow-100 text-yellow-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">Thinking...</span>
              <i class="fas fa-chevron-up toggle-icon text-gray-400"></i>
            </div>
            <div class="thinking-content-wrapper transition-all duration-500 ease-in-out max-h-[400px] overflow-y-auto overflow-x-hidden">
              <div class="thinking-content text-gray-500 text-sm prose max-w-none pt-2 pr-2 whitespace-pre-wrap"></div>
            </div>
          </div>
        </div>
      `
      
      const header = element.querySelector('.thinking-header')
      const wrapper = element.querySelector('.thinking-content-wrapper')
      const icon = element.querySelector('.toggle-icon')
      
      header.addEventListener('click', () => {
        const isExpanded = wrapper.classList.contains('max-h-[400px]')
        if (isExpanded) {
          wrapper.classList.remove('max-h-[400px]')
          wrapper.classList.add('max-h-0')
          icon.classList.add('rotate-180')
        } else {
          wrapper.classList.remove('max-h-0')
          wrapper.classList.add('max-h-[400px]')
          icon.classList.remove('rotate-180')
        }
      })
      
      conversation.appendChild(element)
      return element
    },
    
    // 更新思考过程
    updateThinkingStatus(newText) {
      if (!this.thinkingElement) {
        this.thinkingElement = this.createThinkingElement()
      }
      const wrapper = this.thinkingElement.querySelector('.thinking-content-wrapper')
      const contentDiv = this.thinkingElement.querySelector('.thinking-content')
      if (contentDiv && wrapper) {
        this.thinkingContent += newText
        contentDiv.textContent = this.thinkingContent
        // 核心：自动滚动到底部
        wrapper.scrollTop = wrapper.scrollHeight
      }
    },
    
    // 创建最终答案的消息元素
    createAssistantMessage() {
      const conversation = this.$refs.conversation
      if (!conversation) return null
      
      const element = document.createElement('div')
      element.className = 'message assistant-message p-4 md:p-6 bg-white border border-gray-200 shadow-sm rounded-xl max-w-7xl mx-auto mt-4 fade-in'
      element.innerHTML = `
        <div class="flex items-start space-x-4">
          <div class="text-xl text-blue-500"><i class="fas fa-flask-vial"></i></div>
          <div class="flex-1">
            <span class="inline-block bg-blue-100 text-blue-800 text-xs font-semibold mb-2 px-2.5 py-0.5 rounded-full">Summary</span>
            <div class="answer-content prose prose-blue max-w-none"></div>
          </div>
        </div>
      `
      
      if (this.thinkingElement) {
        this.thinkingElement.insertAdjacentElement('afterend', element)
      } else {
        conversation.appendChild(element)
      }
      return element
    },
    
    // 更新最终答案
    updateAssistantMessage(newChunk) {
      if (!this.assistantMessageElement) return
      
      this.fullAnswerContent += newChunk
      
      if (!this.isRenderScheduled) {
        this.isRenderScheduled = true
        requestAnimationFrame(() => {
          const contentDiv = this.assistantMessageElement.querySelector('.answer-content')
          if (contentDiv) {
            const dirtyHtml = marked.parse(this.fullAnswerContent + '<span class="typing-cursor"></span>')
            const cleanHtml = DOMPurify.sanitize(dirtyHtml)
            contentDiv.innerHTML = cleanHtml
            
            // 由于项目中没有引入MathJax，这里暂时注释掉数学公式渲染
            // 如果需要渲染数学公式，需要在项目中引入MathJax库
            // renderMathInElement(contentDiv, {
            //   delimiters: [
            //     {left: '$$', right: '$$', display: true},
            //     {left: '$', right: '$', display: false}
            //   ]
            // })
          }
          this.isRenderScheduled = false
        })
      }
    },
    
    // 清除结果
    clearResults() {
      this.selectedPatentFile = null
      this.selectedMoleculeFile = null
      this.previewImage = null
      this.analysisResults = null
      this.$refs.patentFileInput.value = ''
      this.$refs.moleculeFileInput.value = ''
      
      // 清除分析状态
      this.thinkingContent = ''
      this.fullAnswerContent = ''
      this.assistantMessageElement = null
      this.thinkingElement = null
      
      // 清除DOM元素
      if (this.$refs.conversation) {
        this.$refs.conversation.innerHTML = ''
      }
      if (this.$refs.statusBar) {
        this.$refs.statusBar.classList.add('hidden')
      }
      if (this.$refs.loadingIndicator) {
        this.$refs.loadingIndicator.classList.add('hidden')
      }
    }
  }
}
</script>

<style scoped>
/* 新增导航栏样式 */
.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background-color: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.navbar-left .back-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px 12px;
}

.navbar-left .back-btn:hover {
  color: #1f2937;
}

.navbar-center .logo-container {
  display: flex;
  align-items: center;
  gap: 8px; /* 将间距从12px减小到8px */
}

.logo-icon {
  font-size: 24px;
  color: #3b82f6;
}

.logo-text h1 {
  margin: 0;
  font-size: 30px;
  /* 设置字体为新罗马 */
  font-family: "Times New Roman", serif;
  /* 使用线性渐变背景并应用到文本 */
  background: linear-gradient(135deg, #2563eb, #0891b2);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: 600;
}

.logo-text p {
  margin: 0;
  font-size: 12px;
  color: gray; /* 将颜色改为灰色 */
}

.navbar-right {
  display: flex;
  gap: 10px;
}

.nav-btn {
  background: none;
  border: none;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.nav-btn:hover {
  color: #1f2937;
}

.patchk-wrapper {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.patchk-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.left-column {
  flex: 2; /* 左侧占 2/5 比例 */
  padding: 20px;
  background-color: #f9fafb;
  overflow-y: auto;
}

.right-column {
  flex: 3; /* 右侧占 3/5 比例 */
  padding: 20px;
  background-color: #ffffff;
  overflow-y: auto;
}

/* 新增的水平容器样式 */
.upload-section-container {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
}

.section {
  padding: 20px;
  background-color: #ffffff;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
}

/* 修改这两个区域的样式，使它们在水平容器中均匀分布 */
.patent-document, .molecular-structure {
  flex: 1;
  margin-bottom: 0;
}

.structure-preview {
  margin-bottom: 30px;
}

h3 {
  margin-top: 0;
  color: #1f2937;
}

.upload-area {
  text-align: center;
}

.upload-icon {
  font-size: 36px;
  margin-bottom: 10px;
}

.blue-icon {
  color: #3b82f6;
}

.green-icon {
  color: #10b981;
}

.subtext {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 20px;
}

.choose-file-btn, .draw-btn, .start-analysis-btn, .clear-results-btn {
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.blue-btn {
  background-color: #3b82f6;
  color: white;
}

.green-btn {
  background-color: #10b981;
  color: white;
}

.btn-group {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 10px;
}

.file-size, .file-formats {
  font-size: 12px;
  color: #6b7280;
}

.preview-area {
  text-align: center;
  padding: 40px 20px;
}

.beaker-icon {
  font-size: 48px;
  color: #e5e7eb;
  margin-bottom: 10px;
}

.start-analysis-btn {
  display: block;
  margin: 20px auto;
  background-color: #93c5fd;
  color: #1e40af;
}

.analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.analysis-header h2 {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1f2937;
}

.clear-results-btn {
  background-color: #ffffff;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}

.analysis-ready {
  text-align: center;
  padding: 60px 20px;
}

.analysis-ready i {
  font-size: 48px;
  color: #e5e7eb;
  margin-bottom: 20px;
}

/* 调整logo-icon样式以适应图片 */
.logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-icon img {
  max-height: 47px;
  width: auto;
}

/* 添加新的样式 */
.selected-file-info {
  margin-top: 15px;
  padding: 10px;
  background-color: #f3f4f6;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selected-file-info p {
  margin: 0;
  font-size: 14px;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-file-btn {
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 12px;
}

.preview-image-container {
  margin-bottom: 15px;
}

.preview-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

/* 为禁用状态的按钮添加样式 */
.start-analysis-btn:disabled {
  background-color: #d1d5db;
  color: #6b7280;
  cursor: not-allowed;
}

/* 调整响应式布局 */
@media (max-width: 768px) {
  .upload-section-container {
    flex-direction: column;
  }
}

/* 添加分析结果相关样式 */
#conversation {
  width: 100%;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.status-bar {
  background-color: #f3f4f6;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  text-align: center;
  color: #4b5563;
  font-size: 14px;
}

.hidden {
  display: none;
}

/* 消息样式 */
.message {
  animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* 思考消息样式 */
.thinking-message .thinking-content-wrapper.max-h-0 {
  max-height: 0;
  overflow: hidden;
}

.thinking-message .toggle-icon {
  transition: transform 0.3s ease;
}

.thinking-message .thinking-content {
  line-height: 1.5;
}

/* 打字光标样式 */
.typing-cursor {
  display: inline-block;
  width: 2px;
  height: 1em;
  background-color: #3b82f6;
  margin-left: 2px;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

/* 富文本样式 */
.prose {
  font-size: 16px;
  line-height: 1.6;
}

.prose h1, .prose h2, .prose h3 {
  color: #1f2937;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}

.prose h1 {
  font-size: 2em;
  font-weight: 700;
}

.prose h2 {
  font-size: 1.5em;
  font-weight: 600;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.3em;
}

.prose h3 {
  font-size: 1.25em;
  font-weight: 600;
}

.prose p {
  margin-bottom: 1em;
}

.prose ul, .prose ol {
  padding-left: 1.5em;
  margin-bottom: 1em;
}

.prose li {
  margin-bottom: 0.5em;
}

.prose strong {
  font-weight: 600;
  color: #1f2937;
}

.prose code {
  background-color: #f3f4f6;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
}

.prose pre {
  background-color: #f3f4f6;
  padding: 1em;
  border-radius: 6px;
  overflow-x: auto;
  margin-bottom: 1em;
}

.prose pre code {
  background-color: transparent;
  padding: 0;
}

/* 蓝色主题富文本样式 */
.prose-blue h1, .prose-blue h2, .prose-blue h3 {
  color: #1e40af;
}

.prose-blue a {
  color: #3b82f6;
  text-decoration: none;
}

.prose-blue a:hover {
  text-decoration: underline;
}
</style>